import { useNode, useEditor } from '@craftjs/core';
import { ROOT_NODE } from '@craftjs/utils';
import ReactDOM from 'react-dom';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import {
  CONTENT_BLOCK_NAME,
  getNodeThemeColorClass,
  ICON_NODE_NAME,
  IMAGE_NODE_NAME,
  EMBED_NODE_NAME,
  TEXT_NODE_NAME,
  bgColorClasses,
  BUTTON_NODE_NAME,
  FORM_NODE_NAME,
  VIDEO_NODE_NAME,
  COUNTDOWN_NODE_NAME,
  POPUP_NODE_NAME,
  PAGE_NODE_NAME,
} from '@/utils/constants';

import { ElementControls } from '@/components/canvas/ElementControls/ElementControls';
import { CraftNodeTitle } from './CraftNodeTitle';
import { selectIsPanningMode, selectTranslateX, selectTranslateY } from '@/store/zoom/selectors';
import {
  selectObjectResizing,
  selectPreviewModeEnabled,
  selectBreakpoint,
} from '@/store/system/selectors';
import {
  getBoundaryDomElement,
  getDomFixedPosition,
  isContainerNode,
  isRelativelyFillableElementV2,
  type ControlsFixedPosition,
} from '@/utils/helperFunc';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import type { TextProps } from '@builder4/shared/components/Text';
import AddContainer from './AddContainer';
import { isFormInputNode } from '@builder4/shared/components/Form/formRegistry';

export const RenderNode = ({ render }: { render: ReactNode }) => {
  const [editTitle, setEditTitle] = useState<boolean>(false);
  const isPanningMode = useAppSelector(selectIsPanningMode);
  const isObjectResizing = useAppSelector(selectObjectResizing);
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const translateX = useAppSelector(selectTranslateX);
  const translateY = useAppSelector(selectTranslateY);
  const elementControlsDom = useRef<HTMLDivElement>(null);
  const titleElementDom = useRef<HTMLDivElement>(null);
  const addIconRef = useRef<HTMLDivElement>(null);
  const [breakpointCount, setBreakpointCount] = useState(0);
  const [originalTitleWidth, setOriginalTitleWidth] = useState(0);
  const [originalElementControlsWidth, setOriginalElementControlsWidth] = useState(0);

  const { id } = useNode();
  const { query, isActive, activeLayer, hoveredLayer } = useEditor((_, query) => ({
    isActive: query.getEvent('selected').contains(id),
    activeLayer: query.getEvent('selected').all()[0],
    hoveredLayer: query.getEvent('hovered').all()[0],
  }));

  const {
    isHover,
    dom,
    nodeName,
    isContainer,
    node,
    isDragged,
    width,
    parentNode,
    isRootContainer,
    nodeProps,
  } = useNode((node) => {
    return {
      isHover: node.events.hovered,
      dom: node.dom,
      nodeName: node.data.displayName,
      parent: node.data.parent,
      isContainer: isContainerNode(node),
      nodeProps: node.data.props as Partial<ContainerProps>,
      isDragged: node.events.dragged,
      width: node.dom?.getBoundingClientRect().width,
      parentNode: node.data.parent ? query.node(node.data.parent as string).get() : null,
      isRootContainer: node.data.parent === ROOT_NODE,
      node,
      layerTitle: node.data.custom.displayName,
    };
  });

  // Get breakpoint-aware prop functions
  const { getBPProp } = useBreakpointAwareProp(nodeName);
  const breakpoint = useAppSelector(selectBreakpoint);

  const position = getBPProp(nodeProps, 'position');
  const left = getBPProp(nodeProps, 'left');
  const top = getBPProp(nodeProps, 'top');
  const bottom = getBPProp(nodeProps, 'bottom');
  const right = getBPProp(nodeProps, 'right');
  // nodeProps is Partial<ContainerProps> for props shared across all nodes,
  // textStyles is Text. Re-casting here instead of making the collector type wider.
  const textStyles = getBPProp(nodeProps as Partial<TextProps>, 'textStyles');

  useEffect(() => {
    if (dom) {
      const el = getBoundaryDomElement(dom);
      if (isActive || activeLayer === id) {
        el.classList.remove('component-hovered');
        el.classList.add('component-selected');
      } else if (isHover || hoveredLayer === id) {
        el.classList.remove('component-selected');
        el.classList.add('component-hovered');
      } else {
        el.classList.remove('component-hovered');
        el.classList.remove('component-selected');
      }
    }
  }, [dom, isActive, isHover, hoveredLayer, activeLayer, id]);

  useEffect(() => {
    if (!dom) return;
    const nextBreakpointCount = getBreakpointCount({
      nodeName,
      isContainer,
      dom,
      originalTitleWidth,
      originalElementControlsWidth,
    });
    setBreakpointCount((prev) => (prev === nextBreakpointCount ? prev : nextBreakpointCount));
  }, [originalTitleWidth, originalElementControlsWidth, width, dom, nodeName, isContainer]);

  useEffect(() => {
    if (!dom) return;
    setTimeout(() => {
      const bounding = getDomFixedPosition(dom);

      if (elementControlsDom.current) {
        const epropKey = { left: 'right', top: 'top' };
        elementControlsDom.current.style.left =
          bounding[epropKey.left as keyof ControlsFixedPosition];
        elementControlsDom.current.style.top =
          bounding[epropKey.top as keyof ControlsFixedPosition];
      }

      if (titleElementDom.current) {
        const tpropKey = { left: 'left', top: 'top' };
        titleElementDom.current.style.left = bounding[tpropKey.left as keyof ControlsFixedPosition];
        titleElementDom.current.style.top = bounding[tpropKey.top as keyof ControlsFixedPosition];
      }

      if (addIconRef.current) {
        addIconRef.current.style.top =
          getParentDirection() === 'column' ? bounding.bottom : bounding.centerY;
        addIconRef.current.style.left =
          getParentDirection() === 'column' ? bounding.centerX : bounding.right;
      }
    });
  }, [
    translateX,
    translateY,
    position,
    left,
    top,
    bottom,
    right,
    textStyles,
    parentNode,
    dom,
    isDragged,
  ]);

  const isRootContainerHovered = () => isHover && isRootContainer;

  const handleTitleEdit = () => {
    setEditTitle(true);
  };

  const getParentDirection = () => {
    if (!parentNode || parentNode.id === ROOT_NODE) {
      return 'column';
    } else {
      return getBreakpointProp(
        parentNode.data.props as Partial<ContainerProps>,
        'flexDirection',
        breakpoint,
      );
    }
  };

  const showTitleOnHover = () => {
    let showTitleComponent = false; //  show if root container is hovered and is not active tree
    if (isRootContainerHovered()) {
      //check if root container is active if yes then dont show title component
      const descendants = query.node(id).descendants(true);
      descendants.unshift(id); //include self
      const isActiveRootContainer = descendants.includes(activeLayer); //root container is active
      showTitleComponent = !isActiveRootContainer;
    }
    return showTitleComponent;
  };

  const shouldShowControls = () => {
    return (
      !previewMode && !isPanningMode && !isObjectResizing && (isActive || isRootContainerHovered())
    );
  };

  const {
    top: domTop,
    right: domRight,
    left: domLeft,
    bottom: domBottom,
    centerX,
    centerY,
  } = getDomFixedPosition(dom);

  const getPortalContainer = () => {
    if (typeof document === 'undefined') return null;
    return document.querySelector('.page-container') as Element | null;
  };

  const portalContainer = getPortalContainer();

  const isFillable = isRelativelyFillableElementV2(node, breakpoint);

  return (
    <>
      {shouldShowControls() && portalContainer
        ? ReactDOM.createPortal(
            <>
              {nodeName !== PAGE_NODE_NAME && (isActive || showTitleOnHover()) && (
                <div
                  data-craft-node-title="true"
                  ref={(el) => {
                    if (el) {
                      titleElementDom.current = el;
                      if (originalTitleWidth === 0) {
                        setOriginalTitleWidth(el.clientWidth);
                      }
                    }
                  }}
                  className={`fixed`}
                  style={{
                    left: domLeft,
                    top: domTop,
                    transform: 'translate(0px , calc(-100% - 8px))',
                  }}
                >
                  <CraftNodeTitle
                    closeEditMode={() => {
                      setEditTitle(false);
                    }}
                    breakpointCount={breakpointCount}
                    className={bgColorClasses[getNodeThemeColorClass(nodeName)] || '!bg-indigo-500'}
                    editTitle={editTitle}
                  />
                </div>
              )}

              {isActive && hasControls(nodeName) && (
                <div
                  className="fixed"
                  ref={(el) => {
                    if (el) {
                      elementControlsDom.current = el;
                      if (originalElementControlsWidth === 0) {
                        setOriginalElementControlsWidth(el.clientWidth);
                      }
                    }
                  }}
                  style={{
                    left: domRight,
                    top: domTop,
                    transform: 'translate(-100% , calc(-100% - 8px))',
                  }}
                >
                  <ElementControls
                    breakpointCount={breakpointCount}
                    className={bgColorClasses[getNodeThemeColorClass(nodeName)] || '!bg-indigo-500'}
                    onTitleEdit={handleTitleEdit}
                  />
                </div>
              )}

              {isActive && isContainer && isFillable && (
                <div
                  ref={addIconRef}
                  className="fixed"
                  style={{
                    top: getParentDirection() === 'column' ? domBottom : centerY,
                    left: getParentDirection() === 'column' ? centerX : domRight,
                    transform:
                      getParentDirection() === 'column'
                        ? 'translate(-50%,5px)'
                        : 'translate(5px , -50%)',
                  }}
                >
                  <AddContainer />
                </div>
              )}
            </>,
            portalContainer,
          )
        : null}
      {render}
    </>
  );
};

// TODO: This function should derive this data from a shared
// source of node data at some point, centralized to one place.
function hasControls(nodeName: string) {
  const nodes = {
    [IMAGE_NODE_NAME]: true,
    [TEXT_NODE_NAME]: true,
    [CONTENT_BLOCK_NAME]: true,
    [BUTTON_NODE_NAME]: true,
    [FORM_NODE_NAME]: true,
    [VIDEO_NODE_NAME]: true,
    [COUNTDOWN_NODE_NAME]: true,
    [ICON_NODE_NAME]: true,
    [POPUP_NODE_NAME]: true,
    [EMBED_NODE_NAME]: true,
  };

  if (isFormInputNode(nodeName)) {
    return true;
  }
  return nodes[nodeName as keyof typeof nodes] || false;
}

function getBreakpointCount({
  nodeName,
  isContainer,
  dom,
  originalTitleWidth,
  originalElementControlsWidth,
}: {
  nodeName: string;
  isContainer: boolean;
  dom: HTMLElement | null;
  originalTitleWidth: number;
  originalElementControlsWidth: number;
}) {
  let count = 0;
  if (!isContainer) {
    count++;
  }
  if (!originalElementControlsWidth || !originalTitleWidth) {
    return count;
  }
  // Now get widths of both and bounding width of node
  let titleWidth = originalTitleWidth;
  let elementControlsWidth = originalElementControlsWidth;
  const boundaryDom = dom ? getBoundaryDomElement(dom) : null;
  const nodeWidth = boundaryDom?.getBoundingClientRect().width || 0;
  const diff = titleWidth + elementControlsWidth - nodeWidth;

  let noGap = diff > 0;
  while (noGap) {
    if (count < 1) {
      titleWidth -= 45; //remove direction btns
    } else if (count < 2) {
      titleWidth -= nodeName.length * 5; //remove title
      if (nodeName === BUTTON_NODE_NAME) {
        titleWidth -= 40;
      }
    } else if (count < 3) {
      elementControlsWidth -= 30; //remove move btn and divider line
    } else {
      elementControlsWidth -= 28; //remove other element controls
    }
    count++;
    noGap = titleWidth + elementControlsWidth - nodeWidth > 0;
  }

  return count;
}
